<script lang="ts">
  import PageSection from '@athena-lib/Page/PageSection.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { HeadingSmall } from '@athena-lib/Text';
  import { Column, Columns } from '@athena-lib/Columns';
  import clsx from 'clsx';
  import HeadingXxSmall from '@athena-lib/Text/HeadingXXSmall/HeadingXXSmall.svelte';
  import Collapse from '@athena-lib/Collapse/Collapse.svelte';
  import { Button } from '@athena-lib/Button';
  import Info from '@athena-lib/Icons/Info.svelte';
  import ArrowDropDown from '@athena-lib/Icons/ArrowDropDown.svelte';
  import ArrowDropUp from '@athena-lib/Icons/ArrowDropUp.svelte';
  import Checkbox from '@athena-lib/Checkbox/Checkbox.svelte';
  import Modal from '@athena-lib/Modal/Modal.svelte';
  import TextField from '@athena-lib/Fields/TextField/TextField.svelte';
  import FieldLabel from '@athena-lib/Fields/FieldLabel/FieldLabel.svelte';
  import * as Type from '@sinclair/typebox';
  import Spinner from '@athena-lib/Icons/Spinner.svelte';
  import HeadingMedium from '@athena-lib/Text/HeadingMedium/HeadingMedium.svelte';
  import { onMount } from 'svelte';
  import { LiveLineItemStickerPreview, StickerPreviewAnimationMode } from '@athena-lib/LivePreview';
  import type { CustomStickerWithId } from '@stickerapp-org/checkout-sdk';
  import AccountTabs from './AccountTabs.svelte';
  import Box from './Box.svelte';
  import ReorderButton from './ReorderButton.svelte';
  import type { OrdersData } from '$lib/server/external/types';
  import { get, post } from '$lib/http';
  import logger from '$lib/logger';

  const ProductItemSchema = Type.Object({
    productFamilyName: Type.String(),
    productName: Type.String(),
    sku: Type.String(),
    attributes: Type.Unknown(),
  });

  const OrderItemSchema = Type.Object({
    sheet_id: Type.String(),
    order_id: Type.String(),
    sheet_name: Type.String(),
    sheet_type: Type.String(),
    material: Type.String(),
    laminate: Type.String(),
    width: Type.String(),
    height: Type.String(),
    quantity: Type.String(),
    price: Type.String(),
    total: Type.String(),
    discount_amount: Type.String(),
    giftcard_amount: Type.String(),
    production_status: Type.String(),
    last_updated: Type.Union([Type.Null(), Type.String()]),
    production_deadline: Type.Union([Type.Null(), Type.String()]),
    description: Type.String(),
    quantityFormatted: Type.String(),
    priceTotalFormatted: Type.String(),
    imgSrc: Type.Optional(Type.Union([Type.Null(), Type.String()])),
    thumbnailHTML: Type.Optional(Type.Union([Type.Null(), Type.String()])),
    visible: Type.Boolean(),
    reorderable: Type.Boolean(),
    quotable: Type.Boolean(),
    figure_id: Type.Optional(Type.Number()),
    error_message: Type.Optional(Type.String()),
    parts: Type.Optional(Type.Array(Type.Unknown())),
    productItem: Type.Optional(ProductItemSchema),
  });

  const PreviewSchema = Type.Object({
    parts: Type.Optional(Type.Array(Type.Unknown())),
    productItem: Type.Optional(ProductItemSchema),
    thumbnailHTML: Type.Optional(Type.Union([Type.Null(), Type.String()])),
  });

  type OrderItem = Type.Static<typeof OrderItemSchema>;

  const OrderSchema = Type.Object({
    thumbnailHTML: Type.Optional(Type.Array(Type.String())),
    previews: Type.Optional(Type.Array(PreviewSchema)),
    orderId: Type.String(),
    title: Type.Optional(Type.String()),
    orderName: Type.String(),
    status: Type.String(),
    orderDate: Type.String(),
    priceFormatted: Type.String(),
    items: Type.Array(OrderItemSchema),
    transactionId: Type.String(),
  });

  type Order = Type.Static<typeof OrderSchema>;

  const { data }: { data: OrdersData } = $props();
  const translations = getCommonTranslations();

  let openReorderMenu = $state<null | { orderId: string; itemsToReorder: Array<OrderItem> }>(null);
  let loadingOrders = $state(false);
  let stateOrders: Array<Order> = $state([]);

  const toggleIsOpen = (orderId: string) => {
    if (openReorderMenu?.orderId === orderId) {
      openReorderMenu = null;
    } else {
      const order = stateOrders.find((x) => x.orderId === orderId);
      openReorderMenu = {
        orderId,
        itemsToReorder: order?.items.filter((x) => x.reorderable) ?? [],
      };
    }
  };

  const shouldReorderItem = (item: OrderItem): boolean => {
    return openReorderMenu?.itemsToReorder.find((x) => x === item) !== undefined;
  };

  const toggleShouldReorderItem = (item: OrderItem) => {
    if (!openReorderMenu) {
      return;
    }
    if (shouldReorderItem(item)) {
      openReorderMenu.itemsToReorder = openReorderMenu.itemsToReorder.filter((x) => x !== item);
    } else {
      openReorderMenu.itemsToReorder.push(item);
    }
  };

  const statusToColor = (status: string) => {
    switch (status.toLocaleLowerCase()) {
      case 'in production':
        return clsx('text-st-green');
      case 'pending':
        return clsx('text-st-blue');
      case 'unpaid':
        return clsx('text-st-red');
      case 'sent':
      default:
        return clsx('text-st-gray-400');
    }
  };
  const commonTranslations = getCommonTranslations();

  const requestQuote = () => {
    if (requestQuoteState === null) {
      return;
    }
    requestQuoteState.requestStatus = 'loading';
    post(
      '/account/create_sheet_request',
      requestQuoteState.formData,
      Type.Object({ success: Type.Literal(true) }),
    )
      .then(() => {
        if (requestQuoteState) {
          requestQuoteState.requestStatus = 'successful';
        }
      })
      .catch(() => {
        if (requestQuoteState) {
          requestQuoteState.requestStatus = 'failed';
        }
      });
  };

  let requestQuoteState = $state<null | {
    formData: {
      order_id: string;
      sheet_id: string;
      quantity: string;
      description: string;
    };
    requestStatus: 'successful' | 'failed' | 'loading' | 'initial';
  }>(null);

  const loadMoreOrders = async (): Promise<void> => {
    loadingOrders = true;
    const limit = 10;
    const offset = stateOrders.length;
    try {
      const response = await get(
        `/account/get_order_cards?items=${limit}&fetchedOrders=${offset}&livePreview=true`,
        Type.Object({ count: Type.Number(), orders: Type.Array(OrderSchema) }),
      );
      loadingOrders = false;
      data.hasMore = response.count > 0;
      stateOrders = stateOrders.concat(response.orders);
    } catch (err) {
      loadingOrders = false;
      logger().error('Error fetching orders', err);
    }
  };
  onMount(loadMoreOrders);
</script>

{#snippet requestQuoteButtonLabel()}
  {#if requestQuoteState?.requestStatus === 'loading'}
    <Spinner sizeClass="mx-auto h-6 w-6" />
  {:else}
    {translations.requestQuote}
  {/if}
{/snippet}

{#if requestQuoteState !== null}
  <Modal
    showModal={requestQuoteState !== null}
    closable={true}
    onClose={() => {
      requestQuoteState = null;
    }}
    size={'sm'}
  >
    <div class="p-10 pt-12">
      {#if requestQuoteState.requestStatus === 'loading' || requestQuoteState.requestStatus === 'initial'}
        <Stack>
          <StackItem>
            <TextField
              type="number"
              label={commonTranslations.quantity}
              bind:value={requestQuoteState.formData.quantity}
            ></TextField>
          </StackItem>
          <StackItem>
            <FieldLabel
              inputId={'additional-information'}
              label={commonTranslations.additionalInformation}
              class="w-full pb-1"
            />
            <textarea
              id="additional-information"
              bind:value={requestQuoteState.formData.description}
              class="comment w-full rounded-md border border-st-gray px-3 py-1 text-st-black"
              rows="5"
            ></textarea>
          </StackItem>
          <StackItem
            ><Button full onClick={requestQuote} size="small" color="white"
              >{@render requestQuoteButtonLabel()}</Button
            ></StackItem
          >
        </Stack>
      {:else if requestQuoteState.requestStatus === 'successful'}
        <Stack>
          <StackItem>
            <HeadingMedium>{translations.success}</HeadingMedium>
          </StackItem>
          <StackItem>
            {translations.wellGetBackToYouAsap}
          </StackItem>
        </Stack>
      {:else if requestQuoteState.requestStatus === 'failed'}
        <Stack>
          <StackItem>
            <HeadingMedium>{translations.anErrorHasOccured}</HeadingMedium>
          </StackItem>
        </Stack>
      {/if}
    </div>
  </Modal>
{/if}

<AccountTabs currentPage="orders" market={data.market} accountData={data.accountData}></AccountTabs>
<PageSection class="grow" backgroundColor="medium">
  <Stack>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <HeadingSmall>{translations['orders']}</HeadingSmall>
        </Column>
      </Columns>
    </StackItem>
    <StackItem>
      <hr class="mb-4 mt-3" />
    </StackItem>

    <StackItem>
      <Stack space="gutter">
        {#each stateOrders as order}
          {@const isOpen = order.orderId === openReorderMenu?.orderId}

          <StackItem>
            <Box>
              <Collapse open={isOpen}>
                {#snippet header()}
                  <Columns collapseBelow="md"
                    ><Column>
                      <div class="min-h-24 max-w-56">
                        {#if order.previews}
                          {#each order.previews as preview, previewId}
                            {#if preview.parts && preview.productItem}
                              <LiveLineItemStickerPreview
                                customStickerPromise={Promise.resolve(
                                  preview.productItem
                                    ? ({
                                        id: `order-id-${order.orderId}-preview-id-${previewId}`,
                                        attributes: preview.productItem.attributes,
                                        parts: preview.parts,
                                        productItem: preview.productItem,
                                      } as CustomStickerWithId)
                                    : undefined,
                                )}
                                animationMode={StickerPreviewAnimationMode.None}
                              />
                            {:else if preview.thumbnailHTML}
                              <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                              {@html preview.thumbnailHTML}
                            {/if}
                          {/each}
                        {:else if order.thumbnailHTML}
                          {#each order.thumbnailHTML as thumbnail}
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html thumbnail}
                          {/each}
                        {/if}
                      </div>
                    </Column>

                    <Column class="self-center">
                      <Stack>
                        <div class=" overflow-hidden text-nowrap">
                          <HeadingXxSmall class="mb-0 truncate" size="xsmall"
                            >{order.title || order.orderName}</HeadingXxSmall
                          >
                          {#if order.title}
                            {order.orderName}<br />
                          {/if}
                          <div class="flex flex-row items-center gap-1">
                            <div class={clsx(statusToColor(order.status), 'text-2xl')}>•</div>
                            {order.status}
                          </div>
                        </div>
                      </Stack>
                    </Column>
                    <Column class="self-center">{order.orderDate}</Column>
                    <Column class="self-center">{order.priceFormatted}</Column>
                    <Column class="self-center">
                      <Stack class="align-end items-end">
                        <StackItem>
                          <Button
                            element="a"
                            class="flex h-10 w-10 flex-row items-center  !px-0 !py-0"
                            color="brand"
                            link={{
                              href: `/customer/view_order/${order.orderId}/${order.transactionId}`,
                            }}
                            size="small"
                          >
                            <Info sizeClass="h-full w-full" />
                          </Button>
                        </StackItem>
                        <StackItem>
                          <Button
                            onClick={() => {
                              toggleIsOpen(order.orderId);
                            }}
                            class="flex h-10 w-10 flex-row content-center items-center justify-center !px-0 !py-0"
                            color="dark"
                            size="small"
                          >
                            {#if isOpen}
                              <ArrowDropUp sizeClass="h-6 w-6"></ArrowDropUp>
                            {:else}
                              <ArrowDropDown sizeClass="h-6 w-6"></ArrowDropDown>
                            {/if}
                          </Button>
                        </StackItem>
                      </Stack>
                    </Column>
                  </Columns>
                {/snippet}
                {#snippet body()}
                  <div class="pb-4 pt-8">
                    <hr />
                  </div>
                  {#each order.items.filter((x) => x.visible) as item, index}
                    <Columns class="md:items-center md:pl-16 md:pr-16" collapseBelow="md">
                      <Column class="min-h-24 max-w-56">
                        {#if item.parts && item.productItem && isOpen}
                          <LiveLineItemStickerPreview
                            customStickerPromise={Promise.resolve(
                              item.productItem
                                ? ({
                                    id: `order-id-${order.orderId}-preview-id-${index}`,
                                    attributes: item.productItem.attributes,
                                    parts: item.parts,
                                    productItem: item.productItem,
                                  } as CustomStickerWithId)
                                : undefined,
                            )}
                            animationMode={StickerPreviewAnimationMode.OnMouseMove}
                          ></LiveLineItemStickerPreview>
                        {:else if item.thumbnailHTML || item.imgSrc}
                          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                          {@html item.thumbnailHTML || item.imgSrc}
                        {/if}
                      </Column>
                      <Column>
                        <Stack>
                          <StackItem>
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html item.description}
                            {#if item.error_message}
                              <div class="text-red-500">
                                {item.error_message}
                              </div>
                            {/if}
                          </StackItem>
                        </Stack>
                      </Column>
                      <Column>{item.quantityFormatted}</Column>
                      <Column>
                        {#if item.reorderable}{item.priceTotalFormatted}{/if}
                      </Column>
                      <Column class="flex justify-end">
                        {#if item.reorderable}
                          <Checkbox
                            label={'Reorder'}
                            id={`${item.sheet_id}-${index}`}
                            checked={shouldReorderItem(item)}
                            onClick={() => {
                              toggleShouldReorderItem(item);
                            }}
                          ></Checkbox>
                        {:else}
                          <Button
                            size="small"
                            disabled={requestQuoteState?.requestStatus === 'loading' ||
                              requestQuoteState?.requestStatus === 'successful'}
                            onClick={() => {
                              requestQuoteState = {
                                formData: {
                                  description: '',
                                  order_id: item.order_id,
                                  quantity: item.quantity,
                                  sheet_id: item.sheet_id,
                                },
                                requestStatus: 'initial',
                              };
                            }}
                            color="white"
                            >{translations.requestQuote}
                          </Button>
                        {/if}
                      </Column>
                    </Columns>
                  {/each}
                  {#if openReorderMenu?.itemsToReorder.length ?? 0 > 0}
                    <ReorderButton
                      orderId={openReorderMenu?.orderId ?? null}
                      sheetIds={openReorderMenu?.itemsToReorder.map((x) => x.sheet_id) ?? []}
                    />
                  {/if}
                {/snippet}
              </Collapse>
            </Box>
          </StackItem>
        {/each}
      </Stack>
    </StackItem>
    {#if loadingOrders}
      <div class="justify-items-center">
        <Spinner sizeClass="w-12 h-12" />
      </div>
    {:else if data.hasMore}
      <div class="flex justify-center">
        <Button
          element="a"
          class="flex w-max flex-row items-center"
          color="brand"
          onClick={loadMoreOrders}
          size="small"
          wide={false}
          full={false}
        >
          Load more
        </Button>
      </div>
    {/if}
  </Stack>
</PageSection>
